import { type FC, useEffect, useMemo } from 'react';

import { StateMessage } from '@matrix-forge/ui/custom/StateMessage';
import { buildReturnUrl, getBackendUrl } from '@/utils/apollo';
import { LogoutSuccess } from './LogoutSuccess';

/**
 * LoginPage for Studio app - Redirects to centralized auth.
 * Uses the server-side /auth/login endpoint which handles Keycloak OAuth flow.
 */
export const LoginPage: FC = () => {
  // Check if we're on the logout page (this can happen if route matching is delayed)
  const isLogoutPage = useMemo(
    () => window.location.hash === '#/logout' || window.location.hash.startsWith('#/logout'),
    []
  );

  // Auto-redirect to centralized auth endpoint
  useEffect(() => {
    // Don't auto-redirect if we're on the logout page
    // This prevents redirect loops after logout when hash route matching is delayed
    if (isLogoutPage) {
      console.log('🚫 LoginPage: Skipping auto-redirect on logout page');
      return;
    }

    // Build return URL - uses full URL for localhost (dev mode), relative path for production
    // After auth, we want to come back to the studio app
    const returnUrl = buildReturnUrl('/studio/', window.location.hash);

    // Get the backend URL (handles dev mode with different ports)
    const backendUrl = getBackendUrl();
    const redirectUrl = `${backendUrl}/auth/login?redirect=${encodeURIComponent(returnUrl)}`;

    console.log('🔐 LoginPage: Redirecting to auth...', redirectUrl);

    // Redirect to the centralized auth endpoint on the Phoenix backend
    window.location.href = redirectUrl;
  }, [isLogoutPage]);

  // If we're on the logout page, show the LogoutSuccess component instead
  if (isLogoutPage) {
    return <LogoutSuccess />;
  }

  return (
    <StateMessage
      isLoading
      title="Redirecting to login..."
      description="You will be redirected to the authentication page."
    />
  );
};
